import { z } from 'zod';
// ---- release-lanes-v2 ----
import { EnvName, LANE_V2_FIELDS, OpRef, PARAMS_BY_TARGET } from './release-params.js';
// ---- end release-lanes-v2 ----
/**
 * `viko.project.yaml` — per-project workflow profile (docs/PLAN.md, "Project onboarding").
 *
 * Every field except `project` and `repos` has a default, and the defaults encode the
 * user's "fast" loop profile. Hard safety limits (production releases, secrets, deleting
 * unmerged work) cannot be relaxed from this file.
 */
export const CONFIG_VERSION = 1;
/** account-projects: v2 holds shared config only; machine names live in machine-local policy. */
export const CONFIG_VERSION_V2 = 2;
const slug = z
    .string()
    .regex(/^[a-z0-9][a-z0-9-]*$/, 'must be lowercase letters, digits and dashes (e.g. "snail-mail")');
export const MachineId = slug.describe('Enrolled machine id, e.g. "mac" or "photon"');
/**
 * v2 capability tag a machine must have (doctor capabilities), e.g. `platform:mac`, `xcode`,
 * `gpu`. Replaces machine names in lanes, secret commands, doctor checks and routing hints.
 */
export const CapabilityTag = z
    .string()
    .regex(/^[a-z0-9][a-z0-9:._-]*$/, 'capability tag, e.g. "platform:mac" or "xcode"');
const requiresList = z.array(CapabilityTag).optional();
const command = z.string().trim().min(1, 'command must not be empty');
const commandList = z.array(command);
/** Map keyed by machine id (per-machine paths / roots). */
const perMachine = (value) => z.record(MachineId, value);
const absPath = z
    .string()
    .min(1)
    .refine((p) => p.startsWith('/') || p.startsWith('~') || /^[A-Za-z]:[\\/]/.test(p), 'must be an absolute path (/…, ~/… or D:\\…)');
// ---------------------------------------------------------------------------
// Repos & machines
// ---------------------------------------------------------------------------
export const Repo = z
    .object({
    id: slug,
    remote: z.string().min(1).optional().describe('git remote URL (origin)'),
    defaultBranch: z.string().min(1).default('main'),
    paths: perMachine(absPath).default({}).describe('Checkout path per machine (v1 only)'),
    lfs: z.boolean().default(false),
})
    .strict();
export const Machines = z
    .object({
    allowed: z.array(MachineId).default(['mac']),
})
    .strict();
// ---------------------------------------------------------------------------
// Worktrees
// ---------------------------------------------------------------------------
const worktreeRoots = perMachine(absPath).default({});
/** Commands run inside a fresh worktree before the agent starts (e.g. link caches). */
const worktreeSetup = commandList
    .optional()
    .describe('Run in each new worktree before the agent starts, e.g. `scripts/worktree.sh warm`');
export const WorktreeStrategy = z
    .discriminatedUnion('strategy', [
    z
        .object({
        strategy: z.literal('worktree'),
        roots: worktreeRoots,
        setup: worktreeSetup,
    })
        .strict(),
    z
        .object({
        strategy: z.literal('pool'),
        size: z.number().int().min(1).max(32).default(3),
        roots: worktreeRoots,
        skipLfsSmudge: z.boolean().default(true),
        setup: worktreeSetup,
    })
        .strict(),
    z
        .object({
        strategy: z.literal('single-lane'),
    })
        .strict(),
])
    .describe('worktree (default) · pool{size} of warm reused worktrees · single-lane (in place)');
// ---------------------------------------------------------------------------
// Landing
// ---------------------------------------------------------------------------
export const MergeMode = z.enum(['ff', 'merge', 'squash', 'pr']);
// ---- landing-modes: path-tiered gates and stacked/integration branches ----
export const LandingTier = z
    .object({
    name: z.string().min(1).optional().describe('Shown in the landing evidence (default: tier N)'),
    when: z
        .object({
        paths: z
            .array(z.string().min(1))
            .min(1)
            .describe('Globs (**, *, ?) matched against the diff paths vs the target'),
    })
        .strict(),
    test: commandList.default([]),
    build: commandList.default([]),
    strictness: z
        .number()
        .int()
        .min(0)
        .max(100)
        .optional()
        .describe('Higher wins when a diff matches several tiers (default 50; the default tier is 50)'),
})
    .strict();
export const IntegrationBranch = z
    .object({
    branch: z.string().min(1).describe('e.g. feature/global-integ'),
    into: z.string().min(1).optional().describe('Where it lands (default: landing.targetBranch)'),
    children: z
        .array(z.string().min(1))
        .default([])
        .describe('Workstream branch globs that land into this branch first'),
})
    .strict();
// ---- end landing-modes ----
export const Landing = z
    .object({
    targetBranch: z.string().min(1).default('main'),
    mergeMode: MergeMode.default('ff'),
    machine: MachineId.optional().describe('Where the landing recipe runs (default: first allowed)'),
    test: commandList.default([]),
    build: commandList.default([]),
    // ---- landing-modes ----
    tiers: z.array(LandingTier).optional(),
    integrationBranches: z.array(IntegrationBranch).optional(),
    // ---- end landing-modes ----
    deployCheck: z
        .object({
        command,
        timeoutSec: z.number().int().positive().default(300),
    })
        .optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Release lanes
// ---------------------------------------------------------------------------
export const ReleaseTarget = z.enum([
    'testflight',
    'web-cloudflare',
    'mac-desktop',
    'windows-desktop',
    'play-internal',
    'steam-branch',
    'garmin-sideload',
    'custom',
    // ---- release-lanes-v2 ----
    'kamal',
    'script',
    // ---- end release-lanes-v2 ----
]);
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'must be HH:MM (24h)');
const TriggerObject = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('on-land') }).strict(),
    z.object({ kind: z.literal('manual') }).strict(),
    z.object({ kind: z.literal('debounce'), minutes: z.number().int().min(1).max(1440) }).strict(),
    z.object({ kind: z.literal('nightly'), at: hhmm.default('03:00') }).strict(),
]);
/**
 * Accepts `on-land` | `manual` | `nightly` | `{ debounce: 15 }` | `{ nightly: "02:30" }`
 * | `{ kind: ..., ... }` and normalizes to `Trigger`.
 */
export const TriggerInput = z
    .union([
    z.enum(['on-land', 'manual', 'nightly']),
    z.object({ debounce: z.number().int().min(1).max(1440) }).strict(),
    z.object({ nightly: hhmm }).strict(),
    TriggerObject,
])
    .transform((t) => {
    if (typeof t === 'string') {
        return t === 'nightly' ? { kind: 'nightly', at: '03:00' } : { kind: t };
    }
    if ('debounce' in t)
        return { kind: 'debounce', minutes: t.debounce };
    if ('nightly' in t)
        return { kind: 'nightly', at: t.nightly };
    return t;
});
export const VersionBump = z.enum(['none', 'build-number', 'patch', 'minor', 'date']);
const laneBase = {
    id: slug,
    name: z.string().min(1).optional().describe('Display name for build cards, e.g. "TestFlight"'),
    machine: MachineId.optional().describe('v1: the machine this lane builds on'),
    /** v2: capability tags the building machine needs (machine-local policy may pin further). */
    requires: requiresList,
    /** Command recipe: today's script. Used as the lane when `params` is absent. */
    recipe: z
        .object({
        commands: commandList,
        cwd: z.string().optional().describe('Relative to the repo root'),
        repo: slug.optional().describe('Repo id (default: first repo)'),
    })
        .strict()
        .default({ commands: [] }),
    trigger: TriggerInput.prefault('on-land'),
    versionBump: VersionBump.default('build-number'),
    /** Production/public lanes (App Store, public web, Steam default branch) always need a passkey tap. */
    production: z.boolean().default(false),
    // ---- release-lanes-v2: secretEnv, secretFiles, verify, resources, environment ----
    ...LANE_V2_FIELDS,
    // ---- end release-lanes-v2 ----
};
const lane = (target, params) => z
    .object({
    ...laneBase,
    target: z.literal(target),
    params: params.optional().describe(`Typed ${target} settings (enables the typed recipe)`),
})
    .strict();
/** One release lane; `params` is typed by `target` (see release-params.ts). */
export const ReleaseLane = z.discriminatedUnion('target', [
    lane('testflight', PARAMS_BY_TARGET.testflight),
    lane('web-cloudflare', PARAMS_BY_TARGET['web-cloudflare']),
    lane('mac-desktop', PARAMS_BY_TARGET['mac-desktop']),
    lane('windows-desktop', PARAMS_BY_TARGET['windows-desktop']),
    lane('play-internal', PARAMS_BY_TARGET['play-internal']),
    lane('steam-branch', PARAMS_BY_TARGET['steam-branch']),
    lane('garmin-sideload', PARAMS_BY_TARGET['garmin-sideload']),
    lane('custom', PARAMS_BY_TARGET.custom),
    // ---- release-lanes-v2 ----
    lane('kamal', PARAMS_BY_TARGET.kamal),
    lane('script', PARAMS_BY_TARGET.script),
    // ---- end release-lanes-v2 ----
]);
// ---------------------------------------------------------------------------
// Loop profile & autonomy
// ---------------------------------------------------------------------------
export const LoopProfile = z.enum(['fast', 'balanced', 'careful']);
export const AutonomyAction = z.enum([
    'add-task',
    'pick-machine',
    'start-run',
    'session-rotation',
    'prune-landed-worktree',
    'new-workstream',
    'land-main',
    'ship-internal',
    'switch-agent',
    'accept-agent-tasks',
    'promote-rules',
    // Hard limits: may only be `ask`.
    'ship-production',
    'delete-unmerged',
    'use-secret',
]);
/** Actions whose policy can never be `auto` (docs/security.md, PLAN "Autonomy"). */
export const HARD_ASK_ACTIONS = [
    'ship-production',
    'delete-unmerged',
    'use-secret',
];
export const AutonomyPolicy = z.enum(['auto', 'ask']);
export const Autonomy = z
    .object({
    level: z.enum(['careful', 'balanced', 'high']).default('balanced'),
    overrides: z.partialRecord(AutonomyAction, AutonomyPolicy).default({}),
})
    .strict();
// ---------------------------------------------------------------------------
// Artifacts, doctor, secrets, routing
// ---------------------------------------------------------------------------
export const DEFAULT_ARTIFACT_GLOBS = [
    'Screenshots/**/*.{png,jpg,jpeg,webp}',
    'Recordings/**/*.{mov,mp4,webm}',
    '.playwright-mcp/**/*.{png,jpeg}',
    '**/*.{png,jpg,mov,mp4}',
];
export const Artifacts = z
    .object({
    globs: z.array(z.string().min(1)).default([...DEFAULT_ARTIFACT_GLOBS]),
    ignore: z
        .array(z.string().min(1))
        .default(['**/node_modules/**', '**/Library/**', '**/DerivedData/**', '**/.git/**']),
    maxBytes: z
        .number()
        .int()
        .positive()
        .default(200 * 1024 * 1024),
    // ---- auto-artifacts ----
    /**
     * Deliverables written in a turn (design/docs/shots/screenshots/artifacts/test-results) or
     * named in the final message: `default` publishes images/HTML under docs/design and shots
     * directories and offers the rest; `auto` publishes all; `ask` offers all; `off` does nothing.
     */
    detect: z.enum(['default', 'auto', 'ask', 'off']).default('default'),
    /** Captured `open`/`xdg-open`/… still run on this machine: `auto` = unless the user is away. */
    openLocally: z.enum(['auto', 'always', 'never']).default('auto'),
    // ---- end auto-artifacts ----
    // ---- visual-review: per-type size limits, commit provenance and sidecar stats ----
    /**
     * Size limit per media type, replacing `maxBytes` for that type (videos are larger than
     * images). Encrypted artifacts are also capped by the transport limit (32 MiB); a larger
     * file is listed with its provenance and sidecar but not uploaded.
     */
    maxBytesByType: z
        .object({
        image: z.number().int().positive().optional(),
        video: z.number().int().positive().optional(),
    })
        .strict()
        .default({}),
    /** Pin every captured image/video to the commit it was captured at (before/after pairing). */
    provenance: z.boolean().default(true),
    /** Sidecar JSON next to a capture: `{path}` = `x.mp4.json`, `{stem}` = `x.json`. */
    sidecars: z.array(z.string().min(1)).default(['{path}.json', '{stem}.json']),
    // ---- end visual-review ----
})
    .strict();
// ---- visual-review: Mac-local review servers reachable from any device ----
/** Loopback http URL a review server listens on (the live-preview tunnel forwards to it). */
const ReviewUrl = z
    .string()
    .min(1)
    .refine((u) => {
    try {
        const x = new URL(u);
        return (x.protocol === 'http:' &&
            ['localhost', '127.0.0.1', '[::1]'].includes(x.hostname.toLowerCase()) &&
            !x.username &&
            !x.password);
    }
    catch {
        return false;
    }
}, 'must be an http://127.0.0.1:<port>/ (or localhost, [::1]) URL');
export const ReviewServer = z
    .object({
    name: z.string().min(1).max(80),
    url: ReviewUrl,
    /** The page writes back (answers, picks): requests are forwarded to the host unchanged. */
    interactive: z.boolean().default(false),
    /**
     * Optional adapter: a JSON endpoint on the same server listing the answers posted on its
     * pages (`[{id, text, question?, at?}]`); new ones become inbox items.
     */
    answers: z
        .object({
        url: ReviewUrl,
        pollSeconds: z.number().int().min(5).max(3600).default(30),
    })
        .strict()
        .optional(),
})
    .strict()
    .refine((r) => !r.answers || new URL(r.answers.url).port === new URL(r.url).port, {
    path: ['answers', 'url'],
    message: 'the answers endpoint must be on the review server (same port)',
});
// ---- end visual-review ----
export const DoctorRequirement = z.enum([
    'xcode',
    'xcode-signing',
    'ios-simulator',
    'iphone-device',
    'unity',
    'gpu',
    'windows',
    'wsl',
    'android-sdk',
    'git-lfs',
    'node',
    'pnpm',
    'bun',
    'rust',
    'dotnet',
    'python',
    'docker',
    'wrangler',
    'fastlane',
    'gh',
    '1password',
    'garmin-sdk',
    'steamcmd',
    'screen-recording',
]);
export const Doctor = z
    .object({
    requires: z.array(DoctorRequirement).default([]),
    custom: z
        .array(z.object({
        name: slug,
        check: command.describe('Exit 0 = ready'),
        // ---- project-adoption: stdout must match; bounded run time ----
        expect: z.string().min(1).optional().describe('Regex the check output must match'),
        timeoutSec: z.number().int().positive().max(600).optional(),
        // ---- end project-adoption ----
        fix: z.string().optional().describe('Remediation hint shown on the Fix card'),
        machine: MachineId.optional(),
        requires: requiresList,
    }))
        .default([]),
})
    .strict();
const opRef = OpRef;
const envName = EnvName;
export const Secrets = z
    .object({
    refs: z
        .array(z.object({
        env: envName,
        ref: opRef,
        description: z.string().optional(),
        /**
         * `project` (default): a vault vikod's 1Password service account may read.
         * `personal`: the user's own vault — read only through the 1Password desktop app
         * (Touch ID), never with the service account.
         */
        vault: z.enum(['project', 'personal']).default('project'),
    }))
        .default([]),
    /** Named commands the agent may run via `viko_run_with_secret`; the value never enters context. */
    commands: z
        .array(z.object({
        name: slug,
        run: command,
        secrets: z.array(envName).min(1),
        machine: MachineId.optional(),
        requires: requiresList,
    }))
        .default([]),
})
    .strict();
/**
 * Tools, services and accounts the project needs to develop, maintain and ship (G10). vikod
 * checks each on every machine (`bin` on PATH, credentials declared/resolvable) and the doctor
 * shows missing ones with setup guidance. Credentials are `secrets.refs` env names, never values.
 */
export const Tools = z.array(z
    .object({
    name: slug,
    kind: z.enum(['cli', 'service', 'account', 'sdk']).default('cli'),
    description: z.string().optional(),
    /** Executable that must be on PATH (checked with `--version`). */
    bin: z
        .string()
        .regex(/^[A-Za-z0-9._+-]+$/)
        .optional(),
    /** Setup guidance: exact install/login command shown as a one-click fix. */
    install: command.optional(),
    docs: z.url().optional(),
    /** Env names from `secrets.refs` this tool needs. */
    secrets: z.array(envName).default([]),
    machine: MachineId.optional(),
})
    .strict());
export const Agent = z.enum(['claude', 'codex']);
export const Routing = z
    .object({
    defaultAgent: Agent.default('claude'),
    hints: z
        .array(z.object({
        labels: z.array(z.string().min(1)).min(1),
        agent: Agent.optional(),
        machine: MachineId.optional(),
        requires: requiresList,
        model: z.string().optional(),
        // g18: agent-neutral tier (fast | standard | deep); `model` wins when both are set
        tier: z.enum(['fast', 'standard', 'deep']).optional(),
    }))
        .default([
        { labels: ['image-gen'], agent: 'codex' },
        { labels: ['goal-loop'], agent: 'codex' },
    ]),
})
    .strict();
// ---------------------------------------------------------------------------
// Progress probe (additive)
// ---------------------------------------------------------------------------
export const ProgressCheckKind = z.enum(['test', 'typecheck', 'lint', 'build', 'custom']);
/**
 * Ground-truth progress probe (docs/protocol.md → Progress). Optional: without it the daemon
 * still reports git state and classifies the agent's own recipe/check commands.
 */
export const Progress = z
    .object({
    /** How often to probe the worktree while a run is busy. */
    intervalSec: z.number().int().min(15).max(3600).default(60),
    /** Minutes without verified progress before a busy run reads as stalled. */
    stallMinutes: z.number().int().min(1).max(240).default(10),
    /** Cheap checks the daemon runs itself after a turn that changed the worktree. */
    checks: z
        .array(z
        .object({
        name: slug,
        command,
        kind: ProgressCheckKind.default('custom'),
        timeoutSec: z.number().int().positive().max(1800).default(120),
    })
        .strict())
        .default([]),
})
    .strict();
// ---- machine-resources: named machine resources commands, landings and lanes need ----
/**
 * A resource on the machine running the work (docs/machine-resources.md): `screen` (the
 * display; only while the user is away or after a one-tap OK), `heavy` (CPU/disk-heavy jobs),
 * or any `kind[:qualifier]` such as `ship:web` or `unity-editor:<project>` (`<project>` is
 * replaced with this project's id). Capacities live in each machine's config, not here.
 */
export const ResourceRef = z
    .string()
    .regex(/^[a-z][a-z0-9-]{0,39}(:([A-Za-z0-9._-]{1,80}|<project>))?$/, 'resource, e.g. "heavy", "screen", "ship:web" or "unity-editor:<project>"');
/** `|`-separated shell globs matched against each command line (`*` spans spaces). */
const CommandPattern = z
    .string()
    .trim()
    .min(1)
    .max(500)
    .refine((p) => p.split('|').every((g) => g.trim().length > 0), 'empty alternative in pattern');
export const ResourceCommandRule = z
    .object({
    match: CommandPattern,
    use: z.array(ResourceRef).min(1).max(8),
    /** Typical duration in minutes (shown on the "Run UI tests now?" approval). */
    estimateMin: z.number().int().positive().max(1440).optional(),
})
    .strict();
export const Resources = z
    .object({
    /** Agent commands that must run under `viko lock` (the guard blocks unwrapped ones). */
    commands: z.array(ResourceCommandRule).max(64).default([]),
    /** Resources a release lane holds while it builds/ships, keyed by lane id. */
    lanes: z.record(slug, z.array(ResourceRef).min(1).max(8)).default({}),
    /** Resources the landing gate's recipe steps hold (default `[heavy]`). */
    landing: z.array(ResourceRef).max(8).optional(),
    /**
     * Commands refused outright ("waiting on machine resources") until they move to `commands`:
     * onboarding holds screen/ship/unity commands so code-only work is safe from day one.
     */
    held: z
        .array(z
        .object({
        match: CommandPattern,
        reason: z.string().max(300).optional(),
        /** What releases the hold, shown with the refusal (e.g. "machine resources"). */
        until: z.string().trim().min(1).max(200).optional(),
    })
        .strict())
        .max(64)
        .default([]),
})
    .strict();
// ---- end machine-resources ----
// ---- project-adoption: adoption, standing rules, holds, owner to-do gates ----
const repoRelPath = z
    .string()
    .min(1)
    .refine((p) => !p.startsWith('/') && !p.startsWith('~') && !/^[A-Za-z]:[\\/]/.test(p), 'must be relative to the repo root (e.g. ".claude/worktrees" or "..")');
/**
 * Existing worktrees and branches, and where new Viko worktrees go. Viko adopts what is there
 * (never prunes or re-creates it) and leaves pruning to the repo's own hooks when `pruner: repo`.
 */
export const Adoption = z
    .object({
    /** Repo-relative globs of directories holding the repo's existing worktrees. */
    worktrees: z.array(repoRelPath).default([]),
    /**
     * `repo`: the repo's own hooks retire landed worktrees (e.g. a reference-transaction +
     * SessionStart pruner); Viko never removes a worktree after landing. `viko`: Viko removes
     * only the worktrees it created.
     */
    pruner: z.enum(['viko', 'repo']).default('viko'),
    /**
     * New Viko worktrees at `<repo>/<root>/<prefix><slug>` instead of Viko's own root, e.g.
     * `{ root: "..", prefix: "repo-" }` for sibling trees that rely on `../Artwork` paths.
     * Machine-local policy (`worktreeRoot`) still wins.
     */
    newWorktrees: z
        .object({
        root: repoRelPath,
        prefix: z
            .string()
            .regex(/^[A-Za-z0-9._-]*$/)
            .default(''),
    })
        .strict()
        .optional(),
})
    .strict();
/** A standing rule injected into every run preamble (naming, copy, lane rules). */
export const StandingRule = z.union([
    z.string().trim().min(1).max(500),
    z
        .object({
        text: z.string().trim().min(1).max(500),
        kind: z.enum(['naming', 'copy', 'lane', 'general']).default('general'),
    })
        .strict(),
]);
/**
 * DEPRECATED alias of `resources.held` (one hold mechanism): parse folds top-level `hold:` into
 * `resources.held` (see `normalizeHolds`), so consumers only ever read `resources.held`.
 * `match` is one or more `|`-separated globs over the command line (`*` = anything).
 */
export const HoldRule = z
    .object({
    match: z.string().trim().min(1).max(500),
    reason: z.string().trim().min(1).max(300),
    /** What releases the hold, shown with the refusal (e.g. "machine resources (package A)"). */
    until: z.string().trim().min(1).max(200).optional(),
})
    .strict();
/**
 * The project's manual owner to-do file (e.g. `docs/owner-todo.md`, items `- [ ] **N. Title**`,
 * "done: #N"). Open items become inbox gates that block tasks labelled `owner-todo#N` (or the
 * labels in `blocks`); `checks` close an item when a command exits 0 or a URL answers 2xx.
 */
export const OwnerTodo = z
    .object({
    path: repoRelPath,
    checks: z
        .array(z
        .object({
        item: z.number().int().positive(),
        run: command.optional(),
        url: z.url().optional(),
        /** Regex the command's stdout (or the URL's body) must match. */
        expect: z.string().min(1).optional(),
    })
        .strict()
        .refine((c) => !!c.run !== !!c.url, 'set exactly one of run or url'))
        .default([]),
    blocks: z
        .array(z
        .object({
        item: z.number().int().positive(),
        labels: z.array(z.string().min(1)).min(1),
    })
        .strict())
        .default([]),
})
    .strict();
// ---- end project-adoption ----
// ---------------------------------------------------------------------------
// Execution (g18): how agents fan out, which models they use, and how they validate
// ---------------------------------------------------------------------------
const ModelOverride = z
    .object({
    agent: Agent,
    tier: z.enum(['fast', 'standard', 'deep']),
    /** CLI model id or alias for this tier (e.g. `sonnet`, `gpt-5-codex`). */
    model: z.string().min(1).max(80).optional(),
    effort: z.enum(['low', 'medium', 'high', 'xhigh', 'max']).optional(),
    /** Extra guidance appended to the profile, e.g. "avoid for Swift UI work". */
    note: z.string().min(1).max(300).optional(),
})
    .strict();
export const Execution = z
    .object({
    /** Write the execution playbook into run preambles and the managed AGENTS.md block. */
    playbook: z.enum(['on', 'off']).default('on'),
    /**
     * Cheap checks agents run while iterating (scoped tests, typecheck of touched packages).
     * The full landing recipe runs once, at landing.
     */
    quickCheck: z.array(z.string().min(1)).max(10).default([]),
    /** Subagents started at a time. */
    batch: z.number().int().min(1).max(10).default(3),
    /** Most subagents running in one wave. */
    maxParallel: z.number().int().min(1).max(20).default(6),
    /** Failures on a tier before moving one tier up. */
    escalation: z
        .object({
        fast: z.number().int().min(1).max(5).default(2),
        standard: z.number().int().min(1).max(5).default(3),
    })
        .prefault({}),
    models: z.array(ModelOverride).max(12).default([]),
})
    .strict();
// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------
const ProjectConfigShape = z
    .object({
    history: z
        .object({ import: z.boolean().default(false), adopt: z.boolean().default(false) })
        .optional(),
    $schema: z.string().optional(),
    version: z
        .union([z.literal(CONFIG_VERSION), z.literal(CONFIG_VERSION_V2)])
        .default(CONFIG_VERSION),
    project: z.object({
        id: slug,
        name: z.string().min(1),
    }),
    repos: z.array(Repo).min(1, 'at least one repo is required'),
    machines: Machines.default({ allowed: ['mac'] }),
    worktree: WorktreeStrategy.default({ strategy: 'worktree', roots: {} }),
    landing: Landing.default({ targetBranch: 'main', mergeMode: 'ff', test: [], build: [] }),
    releaseLanes: z.array(ReleaseLane).default([]),
    loopProfile: LoopProfile.default('fast'),
    autonomy: Autonomy.default({ level: 'balanced', overrides: {} }),
    artifacts: Artifacts.prefault({}),
    doctor: Doctor.prefault({}),
    secrets: Secrets.prefault({}),
    tools: Tools.default([]),
    routing: Routing.prefault({}),
    execution: Execution.optional(), // g18
    progress: Progress.optional(),
    // ---- visual-review ----
    reviewServers: z.array(ReviewServer).max(10).default([]),
    // ---- end visual-review ----
    resources: Resources.optional(), // machine-resources
    // ---- project-adoption ----
    adoption: Adoption.optional(),
    rules: z.array(StandingRule).max(50).default([]),
    /** @deprecated alias of `resources.held`; folded into it at parse time. */
    hold: z.array(HoldRule).max(50).optional(),
    ownerTodo: OwnerTodo.optional(),
    // ---- end project-adoption ----
})
    .strict();
export const ProjectConfig = ProjectConfigShape.strict().superRefine((cfg, ctx) => {
    const v1 = cfg.version === CONFIG_VERSION;
    const allowed = new Set(cfg.machines.allowed);
    const list = [...allowed].join(', ');
    if (v1 && allowed.size === 0)
        ctx.addIssue({
            code: 'custom',
            path: ['machines', 'allowed'],
            message: 'at least one machine must be allowed',
        });
    const checkMachine = (m, path) => {
        if (!v1 && m !== undefined) {
            ctx.addIssue({ code: 'custom', path, message: V2_MACHINE_MESSAGE });
        }
        else if (m !== undefined && !allowed.has(m)) {
            ctx.addIssue({
                code: 'custom',
                path,
                message: `machine "${m}" is not in machines.allowed (${list})`,
            });
        }
    };
    const repoIds = new Set();
    cfg.repos.forEach((repo, i) => {
        if (repoIds.has(repo.id)) {
            ctx.addIssue({
                code: 'custom',
                path: ['repos', i, 'id'],
                message: `duplicate repo id "${repo.id}"`,
            });
        }
        repoIds.add(repo.id);
        for (const m of Object.keys(repo.paths))
            checkMachine(m, ['repos', i, 'paths', m]);
        if (v1 && Object.keys(repo.paths).length === 0) {
            ctx.addIssue({
                code: 'custom',
                path: ['repos', i, 'paths'],
                message: 'at least one machine path is required',
            });
        }
    });
    if (cfg.worktree.strategy !== 'single-lane') {
        for (const m of Object.keys(cfg.worktree.roots))
            checkMachine(m, ['worktree', 'roots', m]);
    }
    checkMachine(cfg.landing.machine, ['landing', 'machine']);
    const laneIds = new Set();
    cfg.releaseLanes.forEach((lane, i) => {
        if (laneIds.has(lane.id)) {
            ctx.addIssue({
                code: 'custom',
                path: ['releaseLanes', i, 'id'],
                message: `duplicate lane id "${lane.id}"`,
            });
        }
        laneIds.add(lane.id);
        if (v1 && lane.machine === undefined)
            ctx.addIssue({
                code: 'custom',
                path: ['releaseLanes', i, 'machine'],
                message: 'v1 lanes need a machine (v2 uses requires)',
            });
        checkMachine(lane.machine, ['releaseLanes', i, 'machine']);
        if (lane.recipe.repo !== undefined && !repoIds.has(lane.recipe.repo)) {
            ctx.addIssue({
                code: 'custom',
                path: ['releaseLanes', i, 'recipe', 'repo'],
                message: `unknown repo "${lane.recipe.repo}" (known: ${[...repoIds].join(', ')})`,
            });
        }
        if (lane.params === undefined && lane.recipe.commands.length === 0) {
            ctx.addIssue({
                code: 'custom',
                path: ['releaseLanes', i, 'recipe', 'commands'],
                message: 'a release lane needs recipe.commands or typed params',
            });
        }
        if (lane.target === 'steam-branch' && lane.params?.branch === 'default' && !lane.production) {
            ctx.addIssue({
                code: 'custom',
                path: ['releaseLanes', i, 'params', 'branch'],
                message: 'the Steam "default" branch is public: set production: true (passkey tap)',
            });
        }
        if (lane.production && lane.trigger.kind !== 'manual') {
            ctx.addIssue({
                code: 'custom',
                path: ['releaseLanes', i, 'trigger'],
                message: 'production lanes must use trigger "manual" — public releases always need a passkey tap',
            });
        }
        // ---- release-lanes-v2: kamal production, remote builder, service-account-only lane secrets ----
        if (lane.target === 'kamal' && lane.params?.env === 'production' && !lane.production) {
            ctx.addIssue({
                code: 'custom',
                path: ['releaseLanes', i, 'params', 'env'],
                message: 'kamal production deploys are public: set production: true (passkey tap)',
            });
        }
        if (lane.target === 'kamal' && lane.params?.remoteBuilder?.machine !== undefined)
            checkMachine(lane.params.remoteBuilder.machine, [
                'releaseLanes',
                i,
                'params',
                'remoteBuilder',
                'machine',
            ]);
        const personal = new Set(cfg.secrets.refs.filter((r) => r.vault === 'personal').map((r) => r.ref));
        const laneRefs = [
            ...Object.entries(lane.secretEnv ?? {}).map(([k, r]) => [
                r,
                ['secretEnv', k],
            ]),
            ...(lane.secretFiles ?? []).map((f, j) => [
                f.ref,
                ['secretFiles', j, 'ref'],
            ]),
        ];
        for (const [ref, at] of laneRefs)
            if (personal.has(ref))
                ctx.addIssue({
                    code: 'custom',
                    path: ['releaseLanes', i, ...at],
                    message: 'lanes read secrets with the 1Password service account only; this ref is in a personal vault (Touch ID)',
                });
        const seenPaths = new Set();
        (lane.secretFiles ?? []).forEach((f, j) => {
            if (seenPaths.has(f.path))
                ctx.addIssue({
                    code: 'custom',
                    path: ['releaseLanes', i, 'secretFiles', j, 'path'],
                    message: `duplicate secret file path "${f.path}"`,
                });
            seenPaths.add(f.path);
        });
        // ---- end release-lanes-v2 ----
    });
    for (const action of HARD_ASK_ACTIONS) {
        if (cfg.autonomy.overrides[action] === 'auto') {
            ctx.addIssue({
                code: 'custom',
                path: ['autonomy', 'overrides', action],
                message: `"${action}" is a hard safety limit and can only be "ask"`,
            });
        }
    }
    const envs = new Set(cfg.secrets.refs.map((r) => r.env));
    cfg.secrets.commands.forEach((c, i) => {
        c.secrets.forEach((s, j) => {
            if (!envs.has(s)) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['secrets', 'commands', i, 'secrets', j],
                    message: `secret "${s}" is not declared in secrets.refs`,
                });
            }
        });
        checkMachine(c.machine, ['secrets', 'commands', i, 'machine']);
    });
    const toolNames = new Set();
    cfg.tools.forEach((t, i) => {
        if (toolNames.has(t.name))
            ctx.addIssue({
                code: 'custom',
                path: ['tools', i, 'name'],
                message: `duplicate tool "${t.name}"`,
            });
        toolNames.add(t.name);
        t.secrets.forEach((s, j) => {
            if (!envs.has(s))
                ctx.addIssue({
                    code: 'custom',
                    path: ['tools', i, 'secrets', j],
                    message: `secret "${s}" is not declared in secrets.refs`,
                });
        });
        checkMachine(t.machine, ['tools', i, 'machine']);
    });
    cfg.routing.hints.forEach((h, i) => {
        checkMachine(h.machine, ['routing', 'hints', i, 'machine']);
    });
    cfg.doctor.custom.forEach((c, i) => {
        checkMachine(c.machine, ['doctor', 'custom', i, 'machine']);
    });
    // ---- machine-resources: lane resources name declared lanes ----
    for (const id of Object.keys(cfg.resources?.lanes ?? {})) {
        if (!laneIds.has(id))
            ctx.addIssue({
                code: 'custom',
                path: ['resources', 'lanes', id],
                message: `unknown release lane "${id}" (known: ${[...laneIds].join(', ') || 'none'})`,
            });
    }
    // ---- end machine-resources ----
    // ---- project-adoption: regexes must compile ----
    const badRegex = (re, path) => {
        if (re === undefined)
            return;
        try {
            new RegExp(re);
        }
        catch {
            ctx.addIssue({ code: 'custom', path, message: `invalid regular expression ${re}` });
        }
    };
    cfg.doctor.custom.forEach((c, i) => {
        badRegex(c.expect, ['doctor', 'custom', i, 'expect']);
    });
    cfg.ownerTodo?.checks.forEach((c, i) => {
        badRegex(c.expect, ['ownerTodo', 'checks', i, 'expect']);
    });
    // ---- end project-adoption ----
});
export const V2_MACHINE_MESSAGE = 'config v2 holds shared settings only: machine names move to machine-local policy (viko project policy)';
/**
 * One hold mechanism: fold the deprecated top-level `hold:` into `resources.held` (canonical),
 * dropping duplicates by `match`. The returned config has no `hold` key.
 */
export function normalizeHolds(cfg) {
    const { hold, ...rest } = cfg;
    if (!hold?.length)
        return rest;
    const base = rest.resources ?? Resources.parse({});
    const seen = new Set(base.held.map((h) => h.match));
    const extra = hold.filter((h) => !seen.has(h.match));
    return { ...rest, resources: { ...base, held: [...base.held, ...extra] } };
}
/** Whether a lane's release needs a fresh passkey assertion. */
export function laneRequiresPasskey(lane) {
    return lane.production;
}
/** Effective policy for landing/shipping given the loop profile (overrides win, hard limits always ask). */
export function effectivePolicy(cfg, action) {
    if (HARD_ASK_ACTIONS.includes(action))
        return 'ask';
    const override = cfg.autonomy.overrides[action];
    if (override)
        return override;
    const profileAuto = {
        fast: ['land-main', 'ship-internal'],
        balanced: ['ship-internal'],
        careful: [],
    };
    if (profileAuto[cfg.loopProfile].includes(action))
        return 'auto';
    const balancedAuto = [
        'add-task',
        'pick-machine',
        'start-run',
        'session-rotation',
        'prune-landed-worktree',
    ];
    if (cfg.autonomy.level === 'careful')
        return 'ask';
    if (balancedAuto.includes(action))
        return 'auto';
    if (cfg.autonomy.level === 'high' &&
        action !== 'accept-agent-tasks' &&
        action !== 'promote-rules') {
        return 'auto';
    }
    return 'ask';
}
//# sourceMappingURL=schema.js.map